import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isUnloadedAnimation,
  selectSelectedAnimation,
  setSelectedAnimation,
} from "../../../app/slices/AnimationsSlice";
import { AnimationDTO } from "../../../dto/AnimationDTO";
import { getAnimationDisplayLabel } from "./animationDisplay";
import { useAnimationRender } from "./useAnimationAssets.hook";

export default function AnimationLibraryCard(props: {
  animation: AnimationDTO;
  onSubmit: () => void;
}) {
  const { animation, onSubmit } = props;
  const dispatch = useDispatch();
  const selectedAnimation = useSelector(selectSelectedAnimation);
  const isUnloaded = isUnloadedAnimation(animation);
  const { renderUri, isFetching: isFetchingRender } = useAnimationRender(
    isUnloaded ? undefined : animation.externalId
  );
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isStillPreviewReady, setIsStillPreviewReady] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const stillPreviewRef = useRef<HTMLCanvasElement>(null);

  const isSelected = useMemo(
    () =>
      !isUnloaded && selectedAnimation?.externalId === animation.externalId,
    [animation.externalId, isUnloaded, selectedAnimation?.externalId]
  );

  useEffect(() => {
    setIsImageLoaded(false);
    setIsStillPreviewReady(false);
    setHasImageError(false);
  }, [renderUri]);

  useEffect(() => {
    if (!renderUri) return;

    let isCancelled = false;
    const image = new Image();

    image.onload = () => {
      if (isCancelled) return;
      const canvas = stillPreviewRef.current;
      if (!canvas) return;
      const context = canvas.getContext("2d");
      if (!context) return;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      setIsStillPreviewReady(true);
    };
    image.onerror = () => {
      if (isCancelled) return;
      setHasImageError(true);
      setIsStillPreviewReady(false);
    };
    image.src = renderUri;

    return () => {
      isCancelled = true;
    };
  }, [renderUri]);

  const onClick = () => {
    dispatch(setSelectedAnimation(animation));
  };

  const onDoubleClick = () => {
    if (!isSelected) return;
    onSubmit();
  };

  const showSkeleton =
    !isUnloaded &&
    ((isFetchingRender && !renderUri) ||
      (!!renderUri &&
        !hasImageError &&
        ((isHovered && !isImageLoaded) ||
          (!isHovered && !isStillPreviewReady))));

  const hoveredRenderUri = isHovered && !hasImageError ? renderUri : undefined;

  const fallbackPreview = (
    <div className="flex h-full w-full items-center justify-center text-[28px] text-creator-text">
      <DirectionsWalkOutlinedIcon fontSize="inherit" color="inherit" />
    </div>
  );

  return (
    <div
      className={classNames(
        "mb-[10px] flex h-[96px] w-full cursor-pointer rounded-[5px] border-2 border-solid bg-creator-surface px-[10px] py-[8px] transition-colors dark:bg-[#171b20]",
        {
          "border-creator-border hover:bg-interaction-text-hoverBackground dark:hover:bg-[#20262d]":
            !isSelected,
          "border-creator-primary bg-interaction-primary-selected dark:bg-[#1b2730]":
            isSelected,
        }
      )}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-full w-[96px] shrink-0 overflow-hidden rounded-[5px] bg-interaction-softBackground dark:bg-[#101316]">
        {showSkeleton && (
          <div className="absolute inset-0 animate-pulse bg-creator-surface" />
        )}
        {renderUri && !hasImageError && (
          <>
            <canvas
              ref={stillPreviewRef}
              aria-label={`${animation.name} paused preview`}
              className={classNames(
                "h-full w-full object-contain p-[6px] transition-opacity",
                {
                  "opacity-0": isHovered || !isStillPreviewReady,
                  "opacity-100": !isHovered && isStillPreviewReady,
                }
              )}
            />
            {hoveredRenderUri && (
              <img
                className={classNames(
                  "absolute inset-0 h-full w-full object-contain p-[6px] transition-opacity",
                  {
                    "opacity-0": !isImageLoaded,
                    "opacity-100": isImageLoaded,
                  }
                )}
                src={hoveredRenderUri}
                alt={animation.name}
                onLoad={() => setIsImageLoaded(true)}
                onError={() => {
                  setHasImageError(true);
                  setIsImageLoaded(false);
                }}
              />
            )}
          </>
        )}
        {(!renderUri || hasImageError) && !showSkeleton && fallbackPreview}
      </div>
      <div className="ml-[14px] flex min-w-0 flex-1 flex-col justify-center select-none">
        <div className="truncate text-[16px] font-bold text-creator-text">
          {animation.name}
        </div>
        <div className="truncate text-[13px] font-medium text-creator-text-sub">
          {getAnimationDisplayLabel(animation)}
        </div>
      </div>
    </div>
  );
}
